import { cloneElement, useEffect, useState } from "react";
import authAPIsService from "../hooks/authAPIs";
import AppLoading from "../components/AppLoading";
import { useLocation, useNavigate } from "react-router-dom";
import GenericMessagePage from "../pages/GenericMessagePage";
import {
  getInternalToken,
  setInternalToken,
  clearInternalToken,
  getUserRole,
  getUserOrganisationId,
  isSameMyakoToken,
  setRedirectURL,
  resolveTenantFromToken,
} from "../utils/tokenUtils";
import { isTenantId } from "../env/tenantConfig";
import { useTenantStore, getActiveTenantId } from "../store/tenantStore";

const ProtectedRoute = ({ children, allowedRoles, isHomePage = false }: { 
  children: JSX.Element[], 
  allowedRoles?: string[],
  isHomePage?: boolean 
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const setTenantId = useTenantStore((s) => s.setTenantId);

  useEffect(() => {
    const validateAndFetchUser = async () => {
      try {
        const searchParams = new URLSearchParams(location.search);
        const myakoToken = searchParams.get("token");
        const tenantParam = searchParams.get("tenant") || searchParams.get("tenantId");

        // 1. Resolve tenant from query param, token payload (iss), or current store
        let currentTenant = getActiveTenantId();
        if (tenantParam) {
          if (isTenantId(tenantParam)) {
            currentTenant = tenantParam;
            setTenantId(tenantParam);
          } else {
            setErrorMessage(`Unknown tenant '${tenantParam}'. Please verify your URL.`);
            setAccessDenied(true);
            setLoading(false);
            return;
          }
        } else if (myakoToken) {
          const detectedFromToken = resolveTenantFromToken(myakoToken);
          if (isTenantId(detectedFromToken)) {
            currentTenant = detectedFromToken;
            setTenantId(detectedFromToken);
          }
        }

        const internalToken = getInternalToken(currentTenant);

        // 2. If Myako token exists in URL
        if (myakoToken) {
          if (!internalToken || internalToken === "undefined") {
            const data = await authAPIsService.validateMyakoToken(myakoToken, currentTenant);
            const verifiedTenant = (isTenantId(data.tenantId) ? data.tenantId : currentTenant);
            if (verifiedTenant !== currentTenant) {
              currentTenant = verifiedTenant;
              setTenantId(verifiedTenant);
            }
            if (data.redirect_url) {
              setRedirectURL(data.redirect_url, currentTenant);
            }
            setInternalToken(data.internalToken, data.role, data.organisationId, myakoToken, data.redirect_url, currentTenant);
          } else {
            const isSameToken = isSameMyakoToken(myakoToken, currentTenant);
            if (!isSameToken) {
              clearInternalToken(currentTenant);
              const data = await authAPIsService.validateMyakoToken(myakoToken, currentTenant);
              const verifiedTenant = (isTenantId(data.tenantId) ? data.tenantId : currentTenant);
              if (verifiedTenant !== currentTenant) {
                currentTenant = verifiedTenant;
                setTenantId(verifiedTenant);
              }
              if (data.redirect_url) {
                setRedirectURL(data.redirect_url, currentTenant);
              }
              setInternalToken(data.internalToken, data.role, data.organisationId, myakoToken, data.redirect_url, currentTenant);
            }
          }
        }

        // 3. Use internal token to verify active user session
        const finalToken = getInternalToken(currentTenant);
        if (!finalToken) throw new Error("No valid token");

        // 4. Check role-based access restrictions
        const userRole = getUserRole(currentTenant);
        if (!userRole) {
          throw new Error("No user role found");
        }
        
        if (allowedRoles && !allowedRoles.includes(userRole)) {
          throw new Error("Access denied - insufficient permissions");
        }

        // Home page access restrictions
        if (isHomePage) {
          if (userRole === "ROLE_COMPANY_ADMIN" || userRole === "ROLE_MANAGER_DASHBOARD_VIEWER") {
            throw new Error("Access denied - insufficient permissions");
          }
        }

        // Admin page access restrictions
        if (!isHomePage && userRole === "ROLE_SUPER_ADMIN") {
          throw new Error("Access denied - insufficient permissions");
        }

        useTenantStore.getState().setIsLoggedIn(true);
      } catch (err: any) {
        const currentTenant = getActiveTenantId();
        clearInternalToken(currentTenant);
        useTenantStore.getState().setIsLoggedIn(false);
        setAccessDenied(true);
      } finally {
        setLoading(false);

        // Clean query parameters from URL (remove token and tenant to leave clean URL)
        const currentParams = new URLSearchParams(location.search);
        if (currentParams.has("token") || currentParams.has("tenant") || currentParams.has("tenantId")) {
          currentParams.delete("token");
          currentParams.delete("tenant");
          currentParams.delete("tenantId");
          const remainingSearch = currentParams.toString();
          const cleanPath = remainingSearch ? `${location.pathname}?${remainingSearch}` : location.pathname;
          navigate(cleanPath, { replace: true });
        }
      }
    };

    validateAndFetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  if (loading) {
    return (
      <GenericMessagePage message="Authenticating user" hideButton={true} styleTitle={{ marginTop: "150px" }}>
        <AppLoading />
      </GenericMessagePage>
    );
  }

  if (accessDenied) {
    return (
      <GenericMessagePage message={errorMessage || "🚫 You don't have access to this page"} />
    );
  }

  const currentTenant = getActiveTenantId();

  // For home page, return the first child (HomePage component)
  if (isHomePage) {
    return cloneElement(children[0], { organisationId: getUserOrganisationId(currentTenant) });
  }

  // For admin pages, return based on role
  const userRole = getUserRole(currentTenant);
  let childIndex = 0; // Default to admin
  
  if (userRole === "ROLE_COMPANY_ADMIN") {
    childIndex = 0; // Admin component
  } else if (userRole === "ROLE_MANAGER_DASHBOARD_VIEWER") {
    childIndex = 1; // Manager component
  }

  return cloneElement(
    children[childIndex],
    { organisationId: getUserOrganisationId(currentTenant) },
  );
};

export default ProtectedRoute;
